/**
 * OfflineForm — contact form shown when the business is unavailable.
 *
 * Supports configurable fields (same shape as pre-chat form fields) so the
 * admin can choose what data to collect offline. Falls back to 3 default
 * fields (name, email, message) when no custom fields are configured.
 */

import { useState } from 'preact/hooks';
import { cn } from '../lib/cn';
import { t } from '../lib/i18n';
import { validateFields } from '../lib/field-validation';
import type { PreChatFormField } from '../client/api-types';
import type { OfflineMessageErrorEvent } from '../client/socket';
import type { OfflineFormStatus } from './useChatState';

/** Default fields when no custom offline_form_fields are configured. */
const DEFAULT_OFFLINE_FIELDS: PreChatFormField[] = [
  {
    name: 'name',
    label: 'name',
    type: 'text',
    required: false,
    placeholder: '',
  },
  {
    name: 'email',
    label: 'email',
    type: 'email',
    required: true,
    placeholder: '',
  },
  {
    name: 'message',
    label: 'message',
    type: 'text',
    required: true,
    placeholder: '',
  },
];

interface OfflineFormProps {
  brandingColor: string;
  brandingName?: string;
  brandingLogo?: string | null;
  /** Custom bold headline. Falls back to `t('offline_title')` when absent. */
  offlineTitle?: string;
  offlineMessage?: string;
  nextAvailableLabel?: string | null;
  /** Custom fields configured by the admin. Falls back to defaults when absent. */
  fields?: PreChatFormField[];
  onSubmit: (data: Record<string, string>) => void;
  /**
   * Submission lifecycle driven by the parent (useChatState). Determines
   * whether the form renders as idle, loading, sent (success screen), or
   * errored (inline banner + retry). Optional so consumers that don't wire
   * the backend ack still work — those get the pre-ack optimistic UX.
   */
  status?: OfflineFormStatus;
  /** Populated when `status === 'error'`. */
  error?: OfflineMessageErrorEvent | null;
  /** Called when the visitor taps "Reintentar" after an error. */
  onRetry?: () => void;
}

function getInputType(field: PreChatFormField): string {
  switch (field.type) {
    case 'email':
      return 'email';
    case 'phone':
      return 'tel';
    default:
      return 'text';
  }
}

function getPlaceholder(field: PreChatFormField): string {
  if (field.placeholder) return field.placeholder;
  // Fallback placeholders for standard fields
  switch (field.name) {
    case 'name':
      return t('offline_form_name');
    case 'email':
      return t('offline_form_email');
    case 'phone':
      return t('placeholder_phone');
    case 'message':
      return t('offline_form_message');
    default:
      return field.label || field.name;
  }
}

export function OfflineForm({
  brandingColor,
  brandingName,
  brandingLogo,
  offlineTitle,
  offlineMessage,
  nextAvailableLabel,
  fields,
  onSubmit,
  status = 'idle',
  // Reserved for code-specific error UX in a follow-up (e.g. distinguishing
  // 'not_connected' from server-side rejections). The banner today is generic.
  error: _error = null,
  onRetry,
}: OfflineFormProps) {
  const formFields =
    fields && fields.length > 0 ? fields : DEFAULT_OFFLINE_FIELDS;

  const [values, setValues] = useState<Record<string, string>>(
    Object.fromEntries(formFields.map((f) => [f.name, ''])),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isSubmitting = status === 'submitting';
  const showError = status === 'error';

  function validate(): boolean {
    const next = validateFields(formFields, values);
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(e: Event) {
    e.preventDefault();
    if (isSubmitting) return;
    if (validate()) {
      const trimmed: Record<string, string> = {};
      for (const [k, v] of Object.entries(values)) {
        const t = v.trim();
        if (t) trimmed[k] = t;
      }
      onSubmit(trimmed);
    }
  }

  function handleChange(name: string, val: string) {
    setValues((prev) => ({ ...prev, [name]: val }));
    if (errors[name]) {
      setErrors((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
    }
  }

  if (status === 'sent') {
    return (
      <div
        class="flex-1 overflow-y-auto flex items-center justify-center p-5 transition-[background] duration-[350ms]"
        style={{
          backgroundColor: 'var(--w-bg)',
          backgroundImage: 'var(--w-bg-pattern)',
          backgroundSize: '20px 20px',
        }}
      >
        <div
          class={cn(
            'w-full max-w-[310px] rounded-[22px] p-6',
            'bg-w-bb-bg',
            'shadow-[0_10px_34px_rgba(0,0,0,0.12)]',
            'animate-pop-in',
            'flex flex-col items-center gap-4 text-center',
          )}
        >
          <div
            class="w-[48px] h-[48px] rounded-full flex items-center justify-center text-white text-[20px]"
            style={{ background: brandingColor }}
          >
            ✓
          </div>
          <p class="m-0 text-[14px] text-w-bb-fg leading-[1.5]">
            {t('offline_form_success')}
          </p>
        </div>
      </div>
    );
  }

  // Determine if the "message" field should be a textarea
  const isTextarea = (field: PreChatFormField) =>
    field.name === 'message' ||
    (field.type === 'text' && field.name.toLowerCase().includes('message'));

  return (
    <div
      class="flex-1 overflow-y-auto flex items-center justify-center p-5 transition-[background] duration-[350ms]"
      style={{
        backgroundColor: 'var(--w-bg)',
        backgroundImage: 'var(--w-bg-pattern)',
        backgroundSize: '20px 20px',
      }}
    >
      <div
        class={cn(
          'w-full max-w-[310px] rounded-[22px] p-6',
          'bg-w-bb-bg',
          'shadow-[0_10px_34px_rgba(0,0,0,0.12)]',
          'animate-pop-in',
          'flex flex-col items-center gap-4',
        )}
      >
        {/* Avatar */}
        {brandingLogo ? (
          <img
            src={brandingLogo}
            alt={brandingName || ''}
            class="w-[56px] h-[56px] rounded-full object-cover"
          />
        ) : (
          <div
            class="w-[56px] h-[56px] rounded-full flex items-center justify-center text-white text-[22px] font-bold"
            style={{ background: brandingColor }}
          >
            {(brandingName || '?').charAt(0).toUpperCase()}
          </div>
        )}

        {/* Title + message */}
        <div class="text-center">
          <p class="m-0 text-[15px] font-bold text-w-bb-fg">
            {offlineTitle || t('offline_title')}
          </p>
          {nextAvailableLabel && (
            <p class="m-0 text-[12.5px] text-w-secondary mt-0.5">
              {t('offline_back_at', { time: nextAvailableLabel })}
            </p>
          )}
          <p class="m-0 text-[12.5px] text-w-secondary mt-2 leading-[1.5]">
            {offlineMessage || t('offline_default_message')}
          </p>
        </div>

        {/* Dynamic form */}
        <form
          onSubmit={handleSubmit}
          novalidate
          class="flex flex-col gap-3 w-full"
        >
          {formFields.map((field) => (
            <div key={field.name} class="flex flex-col gap-1">
              {isTextarea(field) ? (
                <textarea
                  value={values[field.name]}
                  onInput={(e) =>
                    handleChange(
                      field.name,
                      (e.target as HTMLTextAreaElement).value,
                    )
                  }
                  placeholder={`${getPlaceholder(field)}${field.required ? ' *' : ` (${t('optional')})`}`}
                  required={field.required}
                  aria-required={field.required}
                  aria-describedby={
                    errors[field.name] ? `offline-${field.name}-err` : undefined
                  }
                  rows={3}
                  class={cn(
                    'rounded-[13px] px-3 py-2.5 text-[14px] text-w-bb-fg resize-none',
                    'border-[1.5px] outline-none transition-colors duration-150',
                    'bg-w-input placeholder:text-w-secondary/50',
                    errors[field.name]
                      ? 'border-red-400 focus:border-red-500'
                      : 'border-w-border focus:border-[var(--brand)]',
                  )}
                />
              ) : (
                <input
                  type={getInputType(field)}
                  value={values[field.name]}
                  onInput={(e) =>
                    handleChange(
                      field.name,
                      (e.target as HTMLInputElement).value,
                    )
                  }
                  placeholder={`${getPlaceholder(field)}${field.required ? ' *' : ` (${t('optional')})`}`}
                  required={field.required}
                  aria-required={field.required}
                  aria-describedby={
                    errors[field.name] ? `offline-${field.name}-err` : undefined
                  }
                  class={cn(
                    'rounded-[13px] px-3 py-2.5 text-[14px] text-w-bb-fg',
                    'border-[1.5px] outline-none transition-colors duration-150',
                    'bg-w-input placeholder:text-w-secondary/50',
                    errors[field.name]
                      ? 'border-red-400 focus:border-red-500'
                      : 'border-w-border focus:border-[var(--brand)]',
                  )}
                />
              )}
              {errors[field.name] && (
                <p
                  id={`offline-${field.name}-err`}
                  class="m-0 text-xs text-red-500 px-1"
                  role="alert"
                >
                  {errors[field.name]}
                </p>
              )}
            </div>
          ))}

          {showError && (
            <p
              class="m-0 text-[12.5px] text-red-500 px-1 text-center leading-[1.4]"
              role="alert"
            >
              {t('offline_form_error')}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
            onClick={showError && onRetry ? onRetry : undefined}
            style={{ background: brandingColor }}
            class={cn(
              'mt-1 rounded-[14px] px-4 py-2.5 text-[15px] font-semibold text-white',
              'border-0 w-full',
              'hover:opacity-90 active:scale-[0.98] transition-all duration-150',
              'focus-visible:ring-2 focus-visible:ring-offset-1',
              isSubmitting ? 'opacity-70 cursor-wait' : 'cursor-pointer',
            )}
          >
            {isSubmitting
              ? t('offline_form_sending')
              : showError
                ? t('offline_form_retry')
                : t('offline_form_submit')}
          </button>
        </form>
      </div>
    </div>
  );
}
